import type { DialogProps } from "@radix-ui/react-dialog";
import { Command as CommandPrimitive } from "cmdk";

import type { ComponentPropsWithoutRef, HTMLAttributes, Ref } from "react";
import {
  CommandPopoverContent,
  CommandPopover as CommandPopoverRoot,
} from "../components/command-popover";
import { cn } from "../lib/utils";
import Icon from "./icon";

type CommandProps = ComponentPropsWithoutRef<typeof CommandPrimitive> & {
  ref?: Ref<HTMLDivElement>;
};
const Command = ({ className, ref, ...props }: CommandProps) => (
  <CommandPrimitive
    ref={ref}
    className={cn(
      "flex h-full w-full flex-col overflow-hidden rounded bg-popover text-popover-foreground",
      className,
    )}
    {...props}
  />
);
Command.displayName = CommandPrimitive.displayName;

const CommandPopover = ({ children, ...props }: DialogProps) => {
  return (
    <CommandPopoverRoot {...props}>
      <CommandPopoverContent className="overflow-hidden p-0">
        <Command className="[&_[cmdk-group-heading]]:px-2 [&_[cmdk-group-heading]]:font-medium [&_[cmdk-group-heading]]:text-muted-foreground [&_[cmdk-group]:not([hidden])_~[cmdk-group]]:pt-0 [&_[cmdk-group]]:px-2 [&_[cmdk-input-wrapper]_svg]:h-5 [&_[cmdk-input-wrapper]_svg]:w-5 [&_[cmdk-input]]:h-12 [&_[cmdk-item]]:px-2 [&_[cmdk-item]]:py-3 [&_[cmdk-item]_svg]:h-5 [&_[cmdk-item]_svg]:w-5">
          {children}
        </Command>
      </CommandPopoverContent>
    </CommandPopoverRoot>
  );
};

type CommandInputProps = ComponentPropsWithoutRef<
  typeof CommandPrimitive.Input
> & { ref?: Ref<HTMLInputElement> };
const CommandInput = ({ className, ref, ...props }: CommandInputProps) => (
  <div className="flex items-center border-b px-3" cmdk-input-wrapper="">
    <Icon icon="search" className="mr-2 h-4 w-4 shrink-0 opacity-50" />
    <CommandPrimitive.Input
      ref={ref}
      className={cn(
        "flex h-10 w-full rounded-md bg-transparent py-3 text-sm outline-none placeholder:text-muted-foreground disabled:cursor-not-allowed disabled:opacity-50",
        className,
      )}
      {...props}
    />
  </div>
);
CommandInput.displayName = CommandPrimitive.Input.displayName;

type CommandListProps = ComponentPropsWithoutRef<
  typeof CommandPrimitive.List
> & { ref?: Ref<HTMLDivElement> };
const CommandList = ({ className, ref, ...props }: CommandListProps) => (
  <CommandPrimitive.List ref={ref} className={className} {...props} />
);
CommandList.displayName = CommandPrimitive.List.displayName;

type CommandEmptyProps = ComponentPropsWithoutRef<
  typeof CommandPrimitive.Empty
> & { ref?: Ref<HTMLDivElement> };
const CommandEmpty = ({ ref, ...props }: CommandEmptyProps) => (
  <CommandPrimitive.Empty
    ref={ref}
    className="px-2 py-1 text-caption cursor-default"
    {...props}
  />
);
CommandEmpty.displayName = CommandPrimitive.Empty.displayName;

type CommandGroupProps = ComponentPropsWithoutRef<
  typeof CommandPrimitive.Group
> & { ref?: Ref<HTMLDivElement> };
const CommandGroup = ({ className, ref, ...props }: CommandGroupProps) => (
  <CommandPrimitive.Group
    ref={ref}
    className={cn(
      "overflow-hidden text-caption font-title capitalize [&_[cmdk-group-heading]]:px-2 [&_[cmdk-group-heading]]:py-1 [&_[cmdk-group-heading]]:text-xs [&_[cmdk-group-heading]]:font-bold [&_[cmdk-group-heading]]:text-muted-foreground",
      className,
    )}
    {...props}
  />
);
CommandGroup.displayName = CommandPrimitive.Group.displayName;

type CommandSeparatorProps = ComponentPropsWithoutRef<
  typeof CommandPrimitive.Separator
> & { ref?: Ref<HTMLDivElement> };
const CommandSeparator = ({
  className,
  ref,
  ...props
}: CommandSeparatorProps) => (
  <CommandPrimitive.Separator
    ref={ref}
    className={cn("-mx-1 h-px bg-border", className)}
    {...props}
  />
);
CommandSeparator.displayName = CommandPrimitive.Separator.displayName;

type CommandItemProps = ComponentPropsWithoutRef<
  typeof CommandPrimitive.Item
> & { ref?: Ref<HTMLDivElement> };
const CommandItem = ({ className, ref, ...props }: CommandItemProps) => (
  <CommandPrimitive.Item
    ref={ref}
    className={cn(
      "relative flex cursor-default select-none items-center h-6 px-2 py-1 text-caption outline-none data-[disabled=true]:pointer-events-none data-[selected=true]:bg-gray-50 data-[disabled=true]:opacity-50 [&_svg]:pointer-events-none [&_svg]:size-4 [&_svg]:shrink-0",
      className,
    )}
    {...props}
  />
);
CommandItem.displayName = CommandPrimitive.Item.displayName;

const CommandShortcut = ({
  className,
  ...props
}: HTMLAttributes<HTMLSpanElement>) => {
  return (
    <span
      className={cn(
        "ml-auto text-xs tracking-widest text-muted-foreground",
        className,
      )}
      {...props}
    />
  );
};
CommandShortcut.displayName = "CommandShortcut";

export {
  Command,
  CommandPopover,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
  CommandSeparator,
  CommandShortcut,
};
